import { apiEmployeeView } from "@/api/employees/employee-view.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiEmployeeListView,
  ApiEmployeeListViewCreate,
  ApiEmployeeListViewUpdate,
} from "@/types/employees/employee-view.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const employeeViews = createQueryKeys("employeeViews", {
  list: (type: string) => ({
    queryKey: [type],
    queryFn: async () => {
      const result = await apiEmployeeView.getViews(type);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useEmployeeViews = (type: string, enabled = true) => {
  return useQuery({
    ...employeeViews.list(type),
    enabled: enabled && !!type,
  });
};

export const useCreateEmployeeView = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async (body: ApiEmployeeListViewCreate) => {
      const result = await apiEmployeeView.createView(body);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<ApiEmployeeListView[]>(
        employeeViews.list(data.type).queryKey,
        (views = []) => [...views, data]
      );
      queryClient.invalidateQueries({ queryKey: employeeViews.list(data.type).queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useUpdateEmployeeView = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ viewId, body }: { viewId: number; body: ApiEmployeeListViewUpdate }) => {
      const result = await apiEmployeeView.updateView(viewId, body);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<ApiEmployeeListView[]>(
        employeeViews.list(data.type).queryKey,
        (views = []) => views.map((view) => (view.id === data.id ? data : view))
      );
      queryClient.invalidateQueries({ queryKey: employeeViews.list(data.type).queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useDeleteEmployeeView = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ viewId }: { viewId: number; type: string }) => {
      const result = await apiEmployeeView.deleteView(viewId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: employeeViews.list(variables.type).queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useSetDefaultEmployeeView = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ viewId }: { viewId: number; type: string }) => {
      const result = await apiEmployeeView.setDefaultView(viewId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ApiEmployeeListView[]>(
        employeeViews.list(variables.type).queryKey,
        data
      );
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useReorderEmployeeViews = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ type, viewIds }: { type: string; viewIds: number[] }) => {
      const result = await apiEmployeeView.reorderViews({ type, view_ids: viewIds });
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: employeeViews.list(variables.type).queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
